/**
 * Three scenarios, each a pure function returning a full state object.
 * Three scenarios cover roughly 80% of the state variants across the screen
 * set. Hand authoring variants per screen is how prototypes rot.
 *
 *   nina      new account, zero projects. Every empty state, the onboarding path.
 *   sam       two projects: one healthy, one with a failed build. The failure
 *             and Advanced surfaces.
 *   graduate  a project mid transfer with a pending claim.
 *
 * M0 seeds the minimum each scenario needs to be valid. M2 fills in the full
 * narrative from screen spec section 5.
 */

import type {
  Account,
  ActivityEvent,
  AddOn,
  DbCell,
  DbColumn,
  DbColumnType,
  DbConnection,
  DbRow,
  DbTable,
  DemoData,
  Deploy,
  DeployStatus,
  Environment,
  ErrorDetail,
  ErrorGroup,
  FileObj,
  LiveLogLine,
  NewProjectDraft,
  Plan,
  Project,
  QueryResult,
  SavedQuery,
  ScenarioId,
  Secret,
  Setting,
  Traffic,
  TrafficOutcome,
  TrafficPath,
  TrafficPlace,
  TrafficPoint,
  TrafficSpeed,
  TrafficWindow,
  UploadSeedItem,
  Usage,
  UsageState,
} from '../types';
import {
  builtLog,
  builtSteps,
  FAILED_BUILD_LOG,
  FAILED_STEPS,
  TYPO_DIAGNOSIS,
} from './builds';
import { PUBLIC_DOMAIN } from './onboarding';

const DAY = 24 * 60 * 60 * 1000;
const ISO = (msAgo: number) => new Date(Date.now() - msAgo).toISOString();

function env(
  slug: string,
  currentDeployId: string | null,
  settings: Setting[] = [],
  staging?: { slug: string; currentDeployId: string | null; settings?: Setting[] },
): { production: Environment; staging?: Environment } {
  return {
    production: {
      id: `${slug}-production`,
      name: 'production',
      webAddress: `https://${slug}.${PUBLIC_DOMAIN}`,
      currentDeployId,
      // The readable half of S14, and it stays on the environment for the same
      // reason the deploys do: when a second environment arrives, its settings
      // arrive with it rather than being spread across a flat list.
      settings,
    },
    // Present only on a project whose owner asked for one. Its settings are its
    // OWN: nothing here is shared with production, in either direction, which
    // is the fact the two Keys screens exist to make obvious.
    ...(staging
      ? {
          staging: {
            id: `${staging.slug}-staging`,
            name: 'staging' as const,
            webAddress: `https://${staging.slug}.${PUBLIC_DOMAIN}`,
            currentDeployId: staging.currentDeployId,
            settings: staging.settings ?? [],
          },
        }
      : {}),
  };
}

function account(over: Partial<Account>): Account {
  return {
    id: 'acct_1',
    name: 'Nina Okafor',
    email: 'nina@ninaokafor.com',
    plan: 'basic',
    planExpiresAt: null,
    detailLevel: 'simple',
    githubHandle: 'nina',
    isAdmin: false,
    // A brand-new owner, so the design review sees the one combined prompt.
    cliConnected: false,
    // Mock build reviews the whole feature, including staging, so it is on.
    tenantStagingEnabled: true,
    teamsEnabled: true,
    svgitEnabled: true,
    createdAt: ISO(30 * DAY),
    ...over,
  };
}

const HOUR = 60 * 60 * 1000;
const MINUTE = 60 * 1000;
const MB = (n: number) => n * 1024 * 1024;
const GB = (n: number) => n * 1024 * 1024 * 1024;

/**
 * Three resources, and the overview and the usage screen both show exactly
 * these three. What a project used, in raw requests (turned into compute
 * units below) and bytes.
 */
export interface UsedTriple {
  requests: number;
  database: number;
  files: number;
}

const NOTHING: UsedTriple = { requests: 0, database: 0, files: 0 };

/**
 * Basic's allowance, mirroring the `plans` row in apps/cloud's migration 0036:
 * 8,000,000 compute units, 512 MB database, 1 GB files a month, for the whole
 * ACCOUNT. Every demo account is on Basic.
 */
interface PlanLimits {
  computeUnits: number;
  database: number;
  files: number;
}

const BASIC_LIMITS: PlanLimits = {
  computeUnits: 8_000_000,
  database: MB(512),
  files: GB(1),
};

/** The threshold is the traffic light's, stated once, in one place. */
function usageState(used: number, limit: number): UsageState {
  if (used <= 0) return 'none';
  if (used >= limit) return 'over';
  if (used / limit >= 0.8) return 'approaching';
  return 'ok';
}

/**
 * Compute units, mirroring apps/cloud/src/services/usage/compute.ts, which is
 * where the reasoning for these numbers lives. Mirrored here rather than
 * imported for the same reason `usageState` above is: cloud-web is outside the
 * pnpm workspace and the demo fixture has to stand up with no server at all.
 */
const REQUEST_UNITS = 39;
const CPU_MS_UNITS = 0.4;

function computeUnits(requests: number, cpuMs: number): number {
  return Math.round(requests * REQUEST_UNITS + cpuMs * CPU_MS_UNITS);
}

/**
 * The account rollup passes a summed `cpuMs`: the rollup's compute figure has
 * to be the projects' compute added up, not a fresh roll of the same dice.
 * The limits are the same for a project and for the account, because the
 * allowance is one pool per account and a project is measured against it.
 */
function usage(
  projectId: string | null,
  used: UsedTriple = NOTHING,
  limits: PlanLimits = BASIC_LIMITS,
  cpuMs: number = projectId ? usageExtras(projectId, used.requests).cpuMs : 0,
): Usage {
  const units = computeUnits(used.requests, cpuMs);
  const unitLimit = limits.computeUnits;
  return {
    projectId,
    plan: 'basic',
    planLabel: 'Basic',
    planExpiresAt: null,
    ...(projectId === null
      ? {
          credits: { paidUsd: '0.00', promotionalUsd: '0.00', promotionalNextExpiry: null },
          creditsSpent: { totalUsd: '0.00', overageUsd: '0.00', planUsd: '0.00', availableUsd: '0.00' },
        }
      : {}),
    resetsAt: new Date(Date.now() + 11 * DAY).toISOString(),
    metrics: [
      {
        resource: 'compute',
        label: 'Compute Usage',
        used: units,
        limit: unitLimit,
        unit: 'count',
        state: usageState(units, unitLimit),
      },
      {
        resource: 'database-storage',
        label: 'Database',
        used: used.database,
        limit: limits.database,
        unit: 'bytes',
        state: usageState(used.database, limits.database),
      },
      {
        resource: 'file-storage',
        label: 'Files',
        used: used.files,
        limit: limits.files,
        unit: 'bytes',
        state: usageState(used.files, limits.files),
      },
    ],
    // The compute meter hides its inputs; Advanced prints them, and the
    // traffic fixture reads `requests` back off here rather than off a
    // metric that is no longer a request count.
    // The demo has no Stateful Objects, so this stays zero rather than
    // inventing traffic for a capability the fixture does not model.
    advanced: { requests: used.requests, cpuMs, statefulMs: 0, databaseRows: null },
  };
}

/**
 * Mirrors apps/cloud/src/services/usage/usage.ts's rankBreakdown, on every
 * meter: the top three projects by name, the rest as one "others" figure
 * computed as the meter's total minus the named ones, so the parts add up
 * exactly.
 */
function productionFor(
  staging: Project,
  projects: Project[],
): { projectId: string; name: string } {
  const prod = projects.find(
    (p) => p.environment !== 'staging' && p.repo?.name === staging.repo?.name,
  );
  return prod ? { projectId: prod.id, name: prod.name } : { projectId: staging.id, name: staging.name };
}

function withBreakdown(
  rollup: Usage,
  perProject: Array<{ projectId: string; name: string; used: UsedTriple & { compute: number } }>,
): Usage {
  // Sum rows that share a bucket (a staging copy and its production app).
  const buckets = new Map<string, (typeof perProject)[number]>();
  for (const p of perProject) {
    const seen = buckets.get(p.projectId);
    buckets.set(
      p.projectId,
      seen
        ? {
            ...seen,
            used: {
              requests: seen.used.requests + p.used.requests,
              database: seen.used.database + p.used.database,
              files: seen.used.files + p.used.files,
              compute: seen.used.compute + p.used.compute,
            },
          }
        : p,
    );
  }
  const key: Record<string, keyof (UsedTriple & { compute: number })> = {
    compute: 'compute',
    'database-storage': 'database',
    'file-storage': 'files',
  };
  return {
    ...rollup,
    metrics: rollup.metrics.map((metric) => {
      const field = key[metric.resource];
      if (!field) return metric;
      const ranked = [...buckets.values()]
        .map((p) => ({ projectId: p.projectId, name: p.name, used: p.used[field] }))
        .filter((p) => p.used > 0)
        .sort((a, b) => b.used - a.used);
      const top = ranked.slice(0, 3);
      const named = top.reduce((n, p) => n + p.used, 0);
      return {
        ...metric,
        breakdown: {
          projects: top,
          others:
            ranked.length > top.length
              ? { projectCount: ranked.length - top.length, used: Math.max(0, metric.used - named) }
              : null,
        },
      };
    }),
  };
}

/**
 * How long after a claim link expires the unclaimed project is removed.
 *
 * Three days, and the gap is the humane part of the policy rather than an
 * implementation detail: the link stopping work is recoverable (ask for
 * another one), and the removal is not, so the two must never land on the same
 * date. S21's declined path and S22's last rung both print this date, which is
 * why it is one number in one place instead of two dates that agree today.
 */
export const CLEANUP_GRACE_DAYS = 3;

/* ------------------------------------------------------------- builders */

/** Every deploy in the narrative is by the account owner, on main. */
function dep(
  projectId: string,
  sha: string,
  message: string,
  msAgo: number,
  status: DeployStatus,
  durationMs: number,
  over: Partial<Deploy> = {},
): Deploy {
  const steps = builtSteps(durationMs);
  return {
    id: `dep_${sha}`,
    projectId,
    status,
    commitSha: sha,
    commitMessage: message,
    author: 'sam',
    branch: 'main',
    createdAt: ISO(msAgo),
    finishedAt: ISO(Math.max(0, msAgo - durationMs)),
    durationMs,
    isCurrent: status === 'live',
    rollbackable: true,
    errorGroupId: null,
    // The recorded steps are apportioned out of the duration the row already
    // reports, so the deploy detail's ledger adds up to the number a person
    // read on the timeline. The three Advanced facts are on every deploy
    // because Advanced appends: it never has less to show than Simple.
    steps,
    rawLog: builtLog(steps),
    diagnosis: null,
    trigger: 'push',
    buildId: `sv_bld_${sha}9d41`,
    bundleBytes: 2_143_872,
    ...over,
  };
}

function col(name: string, type: DbColumnType, over: Partial<DbColumn> = {}): DbColumn {
  return { name, type, nullable: false, primaryKey: false, ...over };
}

/** The row cap is the plan's, and the grid renders it as a normal condition. */
function table(
  projectId: string,
  name: string,
  rowCount: number,
  msAgo: number,
  columns: DbColumn[],
): DbTable {
  return { name, projectId, rowCount, rowCap: 100, columns, updatedAt: ISO(msAgo) };
}

function file(
  projectId: string,
  path: string,
  bytes: number,
  contentType: string,
  source: FileObj['source'],
  msAgo: number,
): FileObj {
  const name = path.slice(path.lastIndexOf('/') + 1);
  return {
    id: `file_${projectId}_${path.replace(/[^a-z0-9]/gi, '_')}`,
    projectId,
    path,
    name,
    bytes,
    contentType,
    source,
    updatedAt: ISO(msAgo),
    publicUrl: null,
  };
}

function secret(
  projectId: string,
  key: string,
  msAgo: number,
  reserved = false,
  lastDeployId: string | null = null,
): Secret {
  return {
    id: `sec_${projectId}_${key}`,
    projectId,
    key,
    // Write only. The real value never leaves the server after it is set.
    preview: reserved ? 'postgres://' : 'sk_live_',
    reserved,
    createdAt: ISO(msAgo),
    lastUsedAt: null,
    addedBy: 'sam',
    // Which deploy last read this key. It is the Advanced fact on S14, and it
    // is the live deploy rather than the newest one, because a key is picked
    // up by the version that is actually running.
    lastDeployId,
  };
}

/**
 * The two add-ons that exist, per project. Screen spec S15.
 *
 * The four catalogue kinds are deliberately not seeded. A card for a thing
 * that does not exist yet carries no project state, so inventing four records
 * per project would be four rows of fiction the screen would then have to
 * explain. S15 renders the catalogue from its own list and says so.
 */
function addOn(
  projectId: string,
  kind: AddOn['kind'],
  name: string,
  blurb: string,
  connectionVar: string,
  msAgo: number,
  inUseByCode: boolean,
): AddOn {
  return {
    id: `addon_${projectId}_${kind}`,
    projectId,
    kind,
    name,
    status: 'ready',
    connectionNote: null,
    addedAt: ISO(msAgo),
    blurb,
    connectionVar,
    inUseByCode,
  };
}

const DATABASE_BLURB = "Stores your app's information: users, posts, orders, anything you save.";
const STORAGE_BLURB = 'Holds files your app keeps: images, uploads, anything a person sends you.';

/** Both V1 add-ons for one project, which is how every project starts. */
function bothAddOns(
  projectId: string,
  msAgo: number,
  use: { database: boolean; files: boolean },
): AddOn[] {
  return [
    addOn(projectId, 'database', 'Database', DATABASE_BLURB, 'DATABASE_URL', msAgo, use.database),
    addOn(
      projectId,
      'file-storage',
      'File storage',
      STORAGE_BLURB,
      'FILE_STORAGE_URL',
      msAgo,
      use.files,
    ),
  ];
}

/** Activity lines are sentences about things that happened. No ids, no IPs. */
function event(
  projectId: string,
  kind: ActivityEvent['kind'],
  title: string,
  msAgo: number,
  detail: string | null = null,
): ActivityEvent {
  return {
    id: `act_${projectId}_${msAgo}_${kind}`,
    projectId,
    kind,
    title,
    detail,
    at: ISO(msAgo),
    actor: 'sam',
  };
}

function empty(): Omit<DemoData, 'scenarioId' | 'account' | 'projects' | 'usage'> {
  return {
    deploys: [],
    tables: [],
    rows: {},
    files: [],
    secrets: [],
    addons: [],
    activity: [],
    errors: [],
    timelines: [],
    claims: [],
  };
}

/* ------------------------------------------------------------------ nina */

function ninaScenario(): DemoData {
  return {
    scenarioId: 'nina',
    account: account({}),
    projects: [],
    usage: [usage(null)],
    ...empty(),
  };
}

/* ------------------------------------------------------------------- sam */

/**
 * The seeded demo narrative, screen spec section 5. Four projects on one
 * account, because one login has to show every state the product can be in:
 * a healthy app, an app whose newest deploy did not work while the app itself
 * keeps running, an app that has just arrived from Loop, and a paused app.
 *
 * One deviation from the spec table, recorded: spec 5.3 dates d70b845 at
 * "5 days ago" and 5b8a2f0 at "17 August", which puts the older deploy after
 * the newer one. The list here is monotonic, because a timeline that does not
 * run in order is the first thing a reviewer catches.
 */
function samScenario(): DemoData {
  // Herb and Ash is the scenario's ONE project with a staging environment, and
  // it is switched over: pushes to main deploy staging, and the live app only
  // changes on a prod tag. One is enough to rehearse the thing that matters,
  // which is telling the two apart while looking at one of them.
  const herb: Project = {
    id: 'sv_prj_8f2a1c',
    slug: 'herb-and-ash',
    name: 'Herb and Ash',
    status: 'live',
    deployMode: 'staged',
    environment: 'production',
    createdAt: ISO(79 * DAY),
    repo: { provider: 'github', owner: 'sam', name: 'herb-and-ash', branch: 'main', connected: true },
    environments: env(
      'herb-and-ash',
      'dep_a3f19c2',
      [
        { key: 'APP_TIMEZONE', value: 'Asia/Bangkok' },
        { key: 'DEFAULT_SERVINGS', value: '4' },
      ],
      {
        slug: 'herb-and-ash-staging',
        currentDeployId: 'dep_a3f19c2',
        settings: [
          // Deliberately not the same values as production's. A settings pair
          // that matched would teach the opposite of what is true.
          { key: 'APP_TIMEZONE', value: 'Asia/Bangkok' },
          { key: 'DEFAULT_SERVINGS', value: '2' },
        ],
      },
    ),
    claimToken: null,
    region: 'Singapore',
    databaseName: 'db_herb_and_ash',
    fileStoreName: 'sv-files-8f2a1c',
    health: { downMinutes: 0, requestsLastHour: 640, errorsLastHour: 0 },
  };

  /**
   * The staging environment as its own project, because that is exactly what it
   * is: its own web address, its own database, its own files, its own keys, and
   * its own pages at /p/herb-and-ash-staging.
   *
   * It is NOT in the projects list or the switcher menu, both of which filter
   * it out, because an app appearing twice reads as two apps. It is reached from the parent's
   * settings, and the switcher names it with a STAGING marker once you are on
   * it.
   */
  const herbStaging: Project = {
    id: 'sv_prj_8f2a1c_stg',
    slug: 'herb-and-ash-staging',
    name: 'Herb and Ash (staging)',
    status: 'live',
    deployMode: 'direct',
    environment: 'staging',
    createdAt: ISO(9 * DAY),
    repo: { provider: 'github', owner: 'sam', name: 'herb-and-ash', branch: 'main', connected: true },
    environments: env('herb-and-ash-staging', 'dep_a3f19c2', [
      { key: 'APP_TIMEZONE', value: 'Asia/Bangkok' },
      { key: 'DEFAULT_SERVINGS', value: '2' },
    ]),
    claimToken: null,
    region: 'Singapore',
    databaseName: 'db_herb_and_ash_staging',
    fileStoreName: 'sv-files-8f2a1c-stg',
    health: { downMinutes: 0, requestsLastHour: 12, errorsLastHour: 0 },
  };

  const ledger: Project = {
    id: 'sv_prj_c41d70',
    slug: 'studio-ledger',
    name: 'Studio Ledger',
    status: 'didnt-work',
    deployMode: 'direct',
    environment: 'production',
    createdAt: ISO(48 * DAY),
    repo: { provider: 'github', owner: 'sam', name: 'studio-ledger', branch: 'main', connected: true },
    // The live deploy is the one from three days ago, not the newest one. The
    // app is fine and the deploy is not, and that gap is the whole nuance.
    environments: env('studio-ledger', 'dep_b8f2a06', [
      { key: 'APP_TIMEZONE', value: 'Asia/Bangkok' },
      { key: 'INVOICE_PREFIX', value: 'SL' },
    ]),
    claimToken: null,
    region: 'Singapore',
    databaseName: 'db_studio_ledger',
    fileStoreName: 'sv-files-c41d70',
    health: { downMinutes: 0, requestsLastHour: 96, errorsLastHour: 3 },
  };

  const soi: Project = {
    id: 'sv_prj_5b30e9',
    slug: 'soi-cats',
    name: 'Soi Cats',
    status: 'live',
    deployMode: 'direct',
    environment: 'production',
    createdAt: ISO(79 * DAY),
    repo: { provider: 'github', owner: 'sam', name: 'soi-cats', branch: 'main', connected: true },
    environments: env('soi-cats', 'dep_f01a83d', [
      { key: 'APP_TIMEZONE', value: 'Asia/Bangkok' },
    ]),
    // The claim is still open, so the row on the projects list is mid move.
    claimToken: '9f2c-8d41-7a06',
    region: 'Singapore',
    databaseName: 'db_soi_cats',
    fileStoreName: 'sv-files-5b30e9',
    health: { downMinutes: 0, requestsLastHour: 0, errorsLastHour: 0 },
  };

  const wedding: Project = {
    id: 'sv_prj_2c77b4',
    slug: 'wedding-rsvp',
    name: 'Wedding RSVP',
    status: 'paused',
    deployMode: 'direct',
    environment: 'production',
    createdAt: ISO(91 * DAY),
    repo: { provider: 'github', owner: 'sam', name: 'wedding-rsvp', branch: 'main', connected: true },
    environments: env('wedding-rsvp', 'dep_e7d2b14'),
    claimToken: null,
    region: 'Singapore',
    databaseName: 'db_wedding_rsvp',
    fileStoreName: 'sv-files-2c77b4',
    health: { downMinutes: 0, requestsLastHour: 0, errorsLastHour: 0 },
  };

  const tetris: Project = {
    id: 'sv_prj_3e91a0',
    slug: 'tetris',
    name: 'tetris',
    status: 'ready',
    deployMode: 'direct',
    environment: 'production',
    createdAt: ISO(1 * DAY),
    repo: { provider: 'github', owner: 'sam', name: 'tetris', branch: 'main', connected: true },
    environments: env('tetris', null),
    claimToken: null,
    region: 'Singapore',
    databaseName: 'db_tetris',
    fileStoreName: 'sv-files-3e91a0',
    health: { downMinutes: 0, requestsLastHour: 0, errorsLastHour: 0 },
  };

  const deploys: Deploy[] = [
    dep(herb.id, 'a3f19c2', 'Add print view to recipe pages', 2 * HOUR, 'live', 41_000),
    dep(herb.id, 'd70b845', 'Make the search box work on phones', 5 * DAY, 'replaced', 38_000),
    dep(
      herb.id,
      '5b8a2f0',
      'Fix ingredient quantities scaling for half batches',
      8 * DAY,
      'replaced',
      44_000,
    ),
    dep(herb.id, '1e6c093', 'Add saved recipes and a saved list', 9 * DAY, 'replaced', 52_000),
    dep(herb.id, '93af2d1', 'Show cooking time on the recipe cards', 14 * DAY, 'replaced', 37_000),
    dep(herb.id, '0c5e7b8', 'First deploy', 79 * DAY, 'replaced', 66_000),

    // The hero state of the prototype. Its steps, its log and its diagnosis are
    // the verbatim fixtures from screen spec 5.5 and 5.6, and they live in
    // build-failure.ts so nobody improves them by accident. Nothing was
    // published, so the bundle size is zero and the Advanced fact is dropped
    // rather than printed as a number that would claim a build output exists.
    dep(ledger.id, '7c04e1b', 'Fix the currency formatting typo', 12 * MINUTE, 'didnt-work', 24_000, {
      errorGroupId: 'err_build_7c04e1b',
      steps: FAILED_STEPS,
      rawLog: FAILED_BUILD_LOG,
      diagnosis: TYPO_DIAGNOSIS,
      bundleBytes: 0,
    }),
    dep(ledger.id, 'b8f2a06', 'Add a monthly total to the invoice list', 3 * DAY, 'live', 39_000),
    dep(ledger.id, '4d19e73', 'Let invoices be marked as paid', 8 * DAY, 'replaced', 42_000),
    dep(ledger.id, '2af80c5', 'First deploy', 48 * DAY, 'replaced', 58_000),

    dep(soi.id, 'f01a83d', 'First deploy', 79 * DAY, 'live', 34_000),

    dep(wedding.id, 'e7d2b14', 'First deploy', 91 * DAY, 'live', 47_000),
  ];

  const tables: DbTable[] = [
    table(herb.id, 'recipes', 128, 2 * HOUR, [
      col('id', 'number', { primaryKey: true }),
      col('title', 'text'),
      col('servings', 'number'),
      col('minutes', 'number'),
      col('is_public', 'boolean'),
      col('cover_image', 'text', { nullable: true }),
      col('created_at', 'timestamp'),
    ]),
    table(herb.id, 'ingredients', 947, 2 * HOUR, [
      col('id', 'number', { primaryKey: true }),
      col('name', 'text'),
      col('unit', 'text'),
    ]),
    table(herb.id, 'recipe_ingredients', 1_204, 2 * HOUR, [
      col('id', 'number', { primaryKey: true }),
      col('recipe_id', 'number'),
      col('ingredient_id', 'number'),
      col('quantity', 'number'),
    ]),
    table(herb.id, 'saved_recipes', 36, 6 * HOUR, [
      col('id', 'number', { primaryKey: true }),
      col('user_id', 'number'),
      col('recipe_id', 'number'),
      col('created_at', 'timestamp'),
    ]),
    table(herb.id, 'users', 22, 3 * DAY, [
      col('id', 'number', { primaryKey: true }),
      col('email', 'text'),
      col('name', 'text'),
      col('created_at', 'timestamp'),
    ]),
    table(herb.id, '_sv_migrations', 4, 79 * DAY, [
      col('id', 'number', { primaryKey: true }),
      col('name', 'text'),
      col('applied_at', 'timestamp'),
    ]),

    table(ledger.id, 'invoices', 341, 2 * HOUR, [
      col('id', 'number', { primaryKey: true }),
      col('client_id', 'number'),
      col('total', 'number'),
      col('paid', 'boolean'),
      col('issued_at', 'timestamp'),
    ]),
    table(ledger.id, 'line_items', 1_918, 2 * HOUR, [
      col('id', 'number', { primaryKey: true }),
      col('invoice_id', 'number'),
      col('description', 'text'),
      col('amount', 'number'),
    ]),
    table(ledger.id, 'clients', 28, 2 * DAY, [
      col('id', 'number', { primaryKey: true }),
      col('name', 'text'),
      col('email', 'text'),
    ]),
    table(ledger.id, 'attachments', 402, 1 * DAY, [
      col('id', 'number', { primaryKey: true }),
      col('invoice_id', 'number'),
      col('path', 'text'),
    ]),
  ];

  const files: FileObj[] = [
    file(herb.id, 'images/leek-stew.jpg', 421_888, 'image/jpeg', 'uploaded', 2 * HOUR),
    file(herb.id, 'images/focaccia-crumb.jpg', 1_258_291, 'image/jpeg', 'uploaded', 3 * DAY),
    file(herb.id, 'images/tom-kha.jpg', 704_512, 'image/jpeg', 'uploaded', 5 * DAY),
    file(herb.id, 'images/green-mango.jpg', 515_072, 'image/jpeg', 'uploaded', 6 * DAY),
    file(herb.id, 'uploads/receipt-2026-07.pdf', 96_256, 'application/pdf', 'uploaded', 9 * DAY),
    file(herb.id, 'uploads/pantry-list.csv', 3_072, 'text/csv', 'uploaded', 11 * DAY),
    file(herb.id, 'design/assets/logo.svg', 4_096, 'image/svg+xml', 'from-your-code', 2 * HOUR),
    file(herb.id, 'design/assets/og-card.png', 90_112, 'image/png', 'from-your-code', 2 * HOUR),
    file(herb.id, 'design/assets/favicon.png', 6_144, 'image/png', 'from-your-code', 2 * HOUR),

    file(ledger.id, 'design/assets/logo.svg', 3_584, 'image/svg+xml', 'from-your-code', 3 * DAY),
    file(ledger.id, 'uploads/invoice-template.pdf', 122_880, 'application/pdf', 'uploaded', 8 * DAY),

    file(soi.id, 'design/assets/logo.svg', 2_560, 'image/svg+xml', 'from-your-code', 79 * DAY),
    file(soi.id, 'design/assets/og-card.png', 71_680, 'image/png', 'from-your-code', 79 * DAY),
    file(soi.id, 'design/assets/favicon.png', 5_120, 'image/png', 'from-your-code', 79 * DAY),
  ];

  const secrets: Secret[] = [
    secret(herb.id, 'DATABASE_URL', 79 * DAY, true, 'dep_a3f19c2'),
    secret(herb.id, 'RESEND_API_KEY', 69 * DAY, false, 'dep_a3f19c2'),
    secret(herb.id, 'STRIPE_SECRET_KEY', 62 * DAY, false, 'dep_a3f19c2'),

    // The staging environment's own keys, on its own project id, which is the
    // whole of how they are kept apart: the two Keys screens read two different
    // lists and neither can see the other's values.
    //
    // DATABASE_URL is reserved here as it is everywhere, and points at
    // staging's OWN database. RESEND_API_KEY is the same NAME as production's
    // and a different key underneath, which is the ordinary case and the reason
    // nothing copies values across.
    //
    // STRIPE_SECRET_KEY is deliberately ABSENT. Production has it and staging
    // does not, which is what the settings screen warns about before you switch
    // pushes over, and the likeliest reason a first staging build fails.
    secret(herbStaging.id, 'DATABASE_URL', 9 * DAY, true, 'dep_a3f19c2'),
    secret(herbStaging.id, 'RESEND_API_KEY', 9 * DAY, false, 'dep_a3f19c2'),

    // Studio Ledger's keys were last picked up by b8f2a06, the version that is
    // actually running, and not by the newer deploy that did not work. That is
    // the same gap the overview and the deploy timeline are built around.
    secret(ledger.id, 'DATABASE_URL', 48 * DAY, true, 'dep_b8f2a06'),
    secret(ledger.id, 'OPENAI_API_KEY', 39 * DAY, false, 'dep_b8f2a06'),
    secret(ledger.id, 'RESEND_API_KEY', 39 * DAY, false, 'dep_b8f2a06'),
    secret(ledger.id, 'STRIPE_SECRET_KEY', 17 * DAY, false, 'dep_b8f2a06'),

    secret(soi.id, 'DATABASE_URL', 79 * DAY, true, 'dep_f01a83d'),
    secret(wedding.id, 'DATABASE_URL', 91 * DAY, true, 'dep_e7d2b14'),
  ];

  /**
   * Which projects still read which connection out of their code, which is the
   * fact behind the switch that will not move.
   *
   * Herb and Ash serves recipe photos out of file storage and Studio Ledger
   * attaches invoice PDFs, so neither can switch it off. Soi Cats arrived from
   * Loop with three files synced from its repository and nothing that reads
   * them at runtime, so it is the project where switching one off is allowed
   * to work, and where the confirm that enumerates what goes with it is worth
   * looking at.
   */
  const addons: AddOn[] = [
    ...bothAddOns(herb.id, 79 * DAY, { database: true, files: true }),
    ...bothAddOns(ledger.id, 48 * DAY, { database: true, files: true }),
    ...bothAddOns(soi.id, 79 * DAY, { database: true, files: false }),
    ...bothAddOns(wedding.id, 91 * DAY, { database: true, files: false }),
  ];

  const activity: ActivityEvent[] = [
    event(herb.id, 'deploy', 'Deployed "Add print view to recipe pages"', 2 * HOUR),
    event(herb.id, 'database', 'Created a table: saved_recipes', 6 * HOUR),
    event(herb.id, 'deploy', 'Deployed "Make the search box work on phones"', 5 * DAY),
    event(herb.id, 'keys', 'Added a key: RESEND_API_KEY', 69 * DAY),
    event(herb.id, 'add-on', 'File storage switched on', 79 * DAY),
    event(herb.id, 'account', 'Project created', 79 * DAY),

    event(
      ledger.id,
      'deploy',
      'A deploy of "Fix the currency formatting typo" did not work',
      12 * MINUTE,
    ),
    event(ledger.id, 'error', 'Your app started returning errors on some invoices', 3 * DAY),
    event(ledger.id, 'deploy', 'Deployed "Add a monthly total to the invoice list"', 3 * DAY),
    event(ledger.id, 'keys', 'Added a key: STRIPE_SECRET_KEY', 17 * DAY),
    event(ledger.id, 'account', 'Project created', 48 * DAY),

    event(soi.id, 'graduation', 'Soi Cats arrived from Loop', 2 * MINUTE),
    event(soi.id, 'deploy', 'Deployed "First deploy"', 79 * DAY),

    event(wedding.id, 'account', 'Project paused', 47 * DAY),
    event(wedding.id, 'deploy', 'Deployed "First deploy"', 91 * DAY),
  ];

  const errors: ErrorGroup[] = [
    {
      id: 'err_invoice_total',
      projectId: ledger.id,
      deployHash: 'b8f2a06',
      summary: 'Some invoices have no line items, and your app tries to add them up anyway.',
      raw: "TypeError: Cannot read properties of null (reading 'total')",
      location: 'src/routes/invoices.ts line 88',
      count: 14,
      firstSeen: ISO(3 * DAY),
      lastSeen: ISO(20 * MINUTE),
      state: 'new',
    },
    {
      id: 'err_query_timeout',
      projectId: ledger.id,
      deployHash: 'b8f2a06',
      summary: 'One database query took too long and gave up.',
      raw: 'Error: Database query timed out after 5000ms',
      location: 'src/routes/clients.ts line 51',
      count: 2,
      firstSeen: ISO(1 * DAY),
      lastSeen: ISO(1 * DAY),
      state: 'seen',
    },
    {
      id: 'err_monthly_total',
      projectId: ledger.id,
      deployHash: '4d19e73',
      summary: 'A monthly total was read before it was worked out.',
      raw: 'ReferenceError: monthlyTotal is not defined',
      location: 'src/routes/invoices.ts line 12',
      count: 31,
      firstSeen: ISO(9 * DAY),
      lastSeen: ISO(3 * DAY),
      state: 'resolved',
    },
  ];

  const projects = [herb, herbStaging, ledger, tetris, soi, wedding];

  /**
   * What a project's file storage is holding, summed from that project's own
   * file rows instead of typed in a second time.
   *
   * M3's coherence pass caught the reason this exists. Soi Cats arrived with
   * three files synced out of its repository, and its usage triple said zero
   * bytes, so the add-ons card read "3 files, 0 B": two numbers a click apart,
   * from one fixture, disagreeing about the same storage. Deriving the number
   * means the two can never drift again, including when somebody adds a fourth
   * file to the narrative and forgets this line exists.
   *
   * SOI CATS HAS VISITORS AND IS NOT MEASURED, AND THOSE ARE TWO FACTS.
   * Requests here used to be zero, which made one of the two states the whole
   * traffic contract is built to demonstrate unreachable: the staged variant,
   * where visits are being counted and the response reading pipeline has never
   * run, so outcome, speed, pages and places all answer null while the chart
   * above them is full. With zero requests the traffic screen fell to "Nobody
   * has visited yet" instead, which is Wedding RSVP's sentence, and the two
   * projects that were seeded to look nothing alike on the wire drew the same
   * empty box. The number is small on purpose: a handful of people a day found
   * the app while it was still living on Loop.
   *
   * It is a modest month, not a busy one, and it stays well under the free
   * allowance, because nothing else in this project's narrative should change
   * shape over one fixture edit. The last hour is still zero (`health`), so the
   * lamp, the live log tail and the overview all still say what they said.
   */
  const fileBytesOf = (projectId: string) =>
    files.reduce((sum, one) => (one.projectId === projectId ? sum + one.bytes : sum), 0);

  const used: Record<string, UsedTriple> = {
    [herb.id]: { requests: 42_180, database: MB(118), files: MB(340) },
    // A staging environment uses real resources and is COUNTED, in the rollup
    // below and on its own Usage screen. Small, because nobody but its owner
    // visits it.
    [herbStaging.id]: { requests: 906, database: MB(22), files: MB(31) },
    [ledger.id]: { requests: 3_204, database: MB(840), files: MB(46) },
    [tetris.id]: NOTHING,
    [soi.id]: { ...NOTHING, requests: 1_120, files: fileBytesOf(soi.id) },
    [wedding.id]: NOTHING,
  };

  const rollup: UsedTriple = {
    requests: projects.reduce((sum, p) => sum + used[p.id].requests, 0),
    database: projects.reduce((sum, p) => sum + used[p.id].database, 0),
    files: projects.reduce((sum, p) => sum + used[p.id].files, 0),
  };

  // The allowance is one pool per account (apps/cloud's services/usage/usage.ts),
  // so the rollup is measured against the plan's own limits, unscaled.
  const rollupLimits = BASIC_LIMITS;

  return {
    scenarioId: 'sam',
    account: account({
      id: 'acct_2',
      name: 'Sam Ortiz',
      email: 'sam@studioledger.co',
      githubHandle: 'sam',
    }),
    projects,
    usage: [
      withBreakdown(
        usage(
          null,
          rollup,
          rollupLimits,
          projects.reduce((sum, p) => sum + usageExtras(p.id, used[p.id].requests).cpuMs, 0),
        ),
        projects.map((p) => ({
          // Staging folds into the production app it belongs to, as on the
          // server. The fixture has no parent id, so the shared repo stands in.
          ...(p.environment === 'staging'
            ? productionFor(p, projects)
            : { projectId: p.id, name: p.name }),
          used: {
            ...used[p.id],
            compute: computeUnits(
              used[p.id].requests,
              usageExtras(p.id, used[p.id].requests).cpuMs,
            ),
          },
        })),
      ),
      ...projects.map((project) => usage(project.id, used[project.id])),
    ],
    ...empty(),
    deploys,
    tables,
    rows: SAM_ROWS,
    files,
    secrets,
    addons,
    activity,
    errors,
    claims: [
      {
        token: soi.claimToken ?? '',
        projectName: soi.name,
        projectSlug: soi.slug,
        webAddress: soi.environments.production.webAddress,
        arrivingFrom: 'Loop',
        expiresAt: new Date(Date.now() + 23 * DAY).toISOString(),
        cleanupAt: new Date(Date.now() + (23 + CLEANUP_GRACE_DAYS) * DAY).toISOString(),
        state: 'pending',
      },
    ],
  };
}

/* -------------------------------------------------------------- graduate */

function graduateScenario(): DemoData {
  const project: Project = {
    id: 'sv_prj_5b30e9',
    slug: 'soi-cats',
    name: 'Soi Cats',
    status: 'live',
    deployMode: 'direct',
    environment: 'production',
    createdAt: ISO(14 * DAY),
    region: 'Singapore',
    databaseName: 'db_soi_cats',
    fileStoreName: 'sv-files-5b30e9',
    // No visitors yet, which is a real state with its own words, not an
    // empty one: the usage bars read "No traffic yet", never grey as broken.
    health: { downMinutes: 0, requestsLastHour: 0, errorsLastHour: 0 },
    repo: null,
    environments: env('soi-cats', 'dep_g1', [{ key: 'APP_TIMEZONE', value: 'Asia/Bangkok' }]),
    claimToken: 'claim_7fd2',
  };

  return {
    scenarioId: 'graduate',
    account: account({
      id: 'acct_3',
      name: 'Pim Suwan',
      email: 'pim@soicats.org',
      githubHandle: 'pim',
    }),
    projects: [project],
    usage: [usage(null), usage(project.id)],
    ...empty(),
    // A project that arrived from Loop arrives with both connections already
    // made. Nothing here is switched on by the person, which is the whole
    // point of the graduation flow: it is the same app, still wired up.
    addons: bothAddOns(project.id, 14 * DAY, { database: true, files: false }),
    deploys: [
      {
        id: 'dep_g1',
        projectId: project.id,
        status: 'live',
        commitSha: 'a1c9d20',
        commitMessage: 'add the adoption form',
        author: 'pim',
        branch: 'main',
        createdAt: ISO(6 * DAY),
        finishedAt: ISO(6 * DAY - 29_000),
        durationMs: 29_000,
        isCurrent: true,
        rollbackable: true,
        errorGroupId: null,
      },
    ],
    claims: [
      {
        token: 'claim_7fd2',
        projectName: project.name,
        projectSlug: project.slug,
        webAddress: project.environments.production.webAddress,
        arrivingFrom: 'Loop',
        expiresAt: new Date(Date.now() + 21 * DAY).toISOString(),
        cleanupAt: new Date(Date.now() + (21 + CLEANUP_GRACE_DAYS) * DAY).toISOString(),
        state: 'pending',
      },
    ],
  };
}

const SCENARIOS: Record<ScenarioId, () => DemoData> = {
  nina: ninaScenario,
  sam: samScenario,
  graduate: graduateScenario,
};

export function seed(id: ScenarioId): DemoData {
  return SCENARIOS[id]();
}

export const SCENARIO_IDS: ScenarioId[] = ['nina', 'sam', 'graduate'];

/* -------------------------------------------------- the onboarding draft */

/**
 * The app the onboarding flow is in the middle of creating, S3 through S5.
 *
 * It sits beside the scenarios rather than inside DemoData because it is not
 * account state: it is the one project that does not exist yet. S5 reads it,
 * S3 and S4 can adopt it as their handoff, and the day the name field drives
 * the flow for real, this constant becomes that field's default.
 *
 * It is Herb and Ash on purpose. The first build a reviewer watches ends on a
 * project the rest of the prototype already has, so "Go to your project" from
 * the payoff lands on a real overview with real deploys instead of a room
 * nobody furnished.
 *
 * Copy stays in the screens, behind t(). What lives here is machine facts: a
 * commit, a file and line, and a payload written to be pasted into a coding
 * agent. The payload's shape is screen spec 5.6, including the closing
 * sentence that tells the agent not to deploy, which is the most valuable
 * line in it and the kind of thing only the platform knows to say.
 */
/* ----------------------------------------- S16: what sits inside a group */

/**
 * The detail behind one error group: the plain cause, the stack, which frames
 * are the person's own, and the request that hit it. Screen spec 5.8.
 *
 * It is a function rather than a constant so the relative times are computed
 * against the moment the screen asks, which keeps "20 minutes ago" true in a
 * demo that has been open since breakfast.
 */
export function errorDetail(groupId: string): ErrorDetail | null {
  if (groupId === 'err_invoice_total') {
    return {
      cause:
        'src/routes/invoices.ts line 88 reads a total on an invoice that came back empty. It happens when an invoice was created but nothing was added to it yet.',
      stack: `TypeError: Cannot read properties of null (reading 'total')
    at invoiceTotal (src/routes/invoices.ts:88:24)
    at listInvoices (src/routes/invoices.ts:33:11)
    at handleRequest (node_modules/@sv/runtime/dist/router.js:212:19)
    at async serve (node_modules/@sv/runtime/dist/serve.js:64:5)`,
      ownFrames: ['src/routes/invoices.ts:88:24', 'src/routes/invoices.ts:33:11'],
      request: { method: 'GET', path: '/invoices/482', status: 500, at: ISO(20 * MINUTE) },
    };
  }

  if (groupId === 'err_query_timeout') {
    return {
      cause:
        'The query that loads every invoice for a client has no limit on how many rows it asks for. The client Baan Studio now has 402 attachments, and reading all of them took longer than the five seconds a request is allowed.',
      stack: `Error: Database query timed out after 5000ms
    at query (src/lib/db.ts:24:11)
    at clientInvoices (src/routes/clients.ts:51:18)
    at handleRequest (node_modules/@sv/runtime/dist/router.js:212:19)`,
      ownFrames: ['src/lib/db.ts:24:11', 'src/routes/clients.ts:51:18'],
      request: { method: 'GET', path: '/clients/7/invoices', status: 504, at: ISO(1 * DAY) },
    };
  }

  if (groupId === 'err_monthly_total') {
    return {
      cause:
        'The invoice list read a monthly total before the code that works it out had run. The deploy that added the monthly total fixed it, and it has not happened since.',
      stack: `ReferenceError: monthlyTotal is not defined
    at invoiceList (src/routes/invoices.ts:12:9)
    at handleRequest (node_modules/@sv/runtime/dist/router.js:212:19)`,
      ownFrames: ['src/routes/invoices.ts:12:9'],
      request: { method: 'GET', path: '/invoices', status: 500, at: ISO(3 * DAY) },
    };
  }

  return null;
}

/** A machine timestamp for a payload: 2026-08-20 13:52. Never localized. */
function stamp(iso: string): string {
  const at = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/**
 * The copy for agent payload for a runtime error, written to the same shape as
 * the build failure's payload in screen spec 5.6, and for the same three
 * reasons: it names the product and the situation before it names the error,
 * it carries the context rather than just the message, and it closes by
 * telling the agent not to deploy.
 *
 * It lives here rather than in the screen because a payload is a fixture, not
 * a rendering, and because the day a real API writes it, this function is the
 * shape that endpoint has to return.
 */
export function errorFixRequest(group: ErrorGroup, projectName: string): string {
  const detail = errorDetail(group.id);
  const request = detail
    ? `\n\nThe request that hit it:\n\n  ${detail.request.method} ${detail.request.path} responded ${detail.request.status} at ${stamp(detail.request.at)}`
    : '';
  const stack = detail ? `\n\nThe stack:\n\n${detail.stack.replace(/^/gm, '  ')}` : '';
  const deployLine = group.deployHash ? `\nDeploy: ${group.deployHash}` : '';

  return `My app is hosted on SV Cloud. It is throwing an error while it runs and I need you to fix it.

Project: ${projectName}${deployLine}
Error: ${group.raw}
Where: ${group.location}
Seen ${group.count} times, first at ${stamp(group.firstSeen)}, last at ${stamp(group.lastSeen)}.${request}${stack}

Please find the cause, fix it in my code, and commit the fix to main. Pushing
to main deploys automatically, so do not run any deploy commands.`;
}

/* --------------------------------------------- S17: the live log tail pool */

/**
 * The lines an app is printing right now, per project.
 *
 * The tail is written by cycling this pool against the clock, so the stream
 * runs as long as somebody watches it without the fixture having to be a
 * thousand lines long. The gaps are the rhythm: a healthy app answers a burst
 * of requests and then goes quiet, and a metronome would read as fake.
 *
 * Soi Cats and Wedding RSVP return nothing on purpose. Nothing arriving right
 * now, and paused, are both real states, and a log tail that invented traffic
 * for them would contradict every other screen in the product. Soi Cats has
 * had visitors this month and has none in this minute, which is what its
 * health record says (`requestsLastHour: 0`) and what the tail should show: a
 * quiet minute is not an empty history.
 */
export function liveLogLines(slug: string): LiveLogLine[] {
  if (slug === 'herb-and-ash') {
    return [
      { level: 'request', gapMs: 900, text: 'GET /recipes 200 in 24ms' },
      { level: 'request', gapMs: 1_400, text: 'GET /recipes/charred-leek-and-butter-bean-stew 200 in 31ms' },
      { level: 'app', gapMs: 700, text: 'cache hit for recipes:page:1' },
      { level: 'request', gapMs: 2_100, text: 'GET /images/leek-stew.jpg 200 in 11ms' },
      { level: 'request', gapMs: 800, text: 'GET /recipes/overnight-focaccia 200 in 28ms' },
      { level: 'app', gapMs: 1_600, text: 'saved recipe 128 for user 14' },
      { level: 'request', gapMs: 600, text: 'POST /saved 201 in 46ms' },
      { level: 'request', gapMs: 2_800, text: 'GET /search?q=leek 200 in 52ms' },
      { level: 'app', gapMs: 900, text: 'search matched 3 recipes in 9ms' },
      { level: 'request', gapMs: 1_200, text: 'GET /recipes/tom-kha-with-roasted-squash 200 in 26ms' },
      { level: 'request', gapMs: 3_400, text: 'GET /robots.txt 200 in 4ms' },
      { level: 'app', gapMs: 1_100, text: 'database pool idle, 2 of 5 connections open' },
    ];
  }

  if (slug === 'studio-ledger') {
    return [
      { level: 'request', gapMs: 1_100, text: 'GET /invoices 200 in 38ms' },
      { level: 'app', gapMs: 800, text: 'loaded 341 invoices for account 7' },
      { level: 'request', gapMs: 1_900, text: 'GET /invoices/481 200 in 22ms' },
      { level: 'request', gapMs: 1_300, text: 'GET /invoices/482 500 in 19ms' },
      {
        level: 'error',
        gapMs: 200,
        text: "TypeError: Cannot read properties of null (reading 'total') at invoiceTotal (src/routes/invoices.ts:88:24)",
      },
      { level: 'request', gapMs: 2_600, text: 'POST /invoices/481/paid 200 in 41ms' },
      { level: 'app', gapMs: 700, text: 'invoice 481 marked as paid' },
      { level: 'request', gapMs: 1_800, text: 'GET /clients 200 in 33ms' },
      { level: 'request', gapMs: 2_200, text: 'GET /clients/7/invoices 504 in 5002ms' },
      { level: 'error', gapMs: 200, text: 'Error: Database query timed out after 5000ms at query (src/lib/db.ts:24:11)' },
      { level: 'request', gapMs: 3_100, text: 'GET /invoices 200 in 36ms' },
      { level: 'app', gapMs: 1_400, text: 'database pool idle, 1 of 5 connections open' },
    ];
  }

  return [];
}

/* ------------------------------------------------ S18: the day by day view */

/** A small deterministic generator, so a screenshot taken twice matches. */
function roll(salt: string): () => number {
  let state = 2166136261;
  for (let i = 0; i < salt.length; i += 1) {
    state ^= salt.charCodeAt(i);
    state = Math.imul(state, 16777619);
  }
  return () => {
    state = Math.imul(state ^ (state >>> 15), state | 1);
    state ^= state + Math.imul(state ^ (state >>> 7), state | 61);
    return ((state ^ (state >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A per day series that sums to the total. Requests are counted per day, so
 * the numbers on the breakdown add up to the number on the bar, which is the
 * only reason a person opens the breakdown.
 */
export function dailyCounts(total: number, days: number, salt: string): number[] {
  if (total <= 0) return Array.from({ length: days }, () => 0);
  const next = roll(salt);
  const weights = Array.from({ length: days }, () => 0.4 + next());
  const sum = weights.reduce((a, b) => a + b, 0);
  const out = weights.map((weight) => Math.round((weight / sum) * total));
  const drift = total - out.reduce((a, b) => a + b, 0);
  out[out.length - 1] += drift;
  return out;
}

/**
 * A per day series that ends at the total and never goes backwards. Storage is
 * a level, not an amount spent, and a breakdown that printed daily deltas for
 * a bar labelled 118 MB would be two different measurements wearing one name.
 */
export function dailyLevels(total: number, days: number, salt: string): number[] {
  if (total <= 0) return Array.from({ length: days }, () => 0);
  const next = roll(salt);
  const steps = Array.from({ length: days }, () => next());
  const sum = steps.reduce((a, b) => a + b, 0);
  // Two thirds of the total was already there at the start of the window, so
  // the series reads as a store that grew rather than one that appeared.
  const base = total * 0.66;
  let running = base;
  return steps.map((step) => {
    running += ((total - base) * step) / sum;
    return Math.round(running);
  });
}

/**
 * The two meters the vocabulary contract marks Advanced only. They are metered
 * by the plan and they are never shown in Simple, because neither one answers
 * the question Simple exists to answer.
 *
 * Both are derived from the request count rather than invented, because an app
 * that has had no visitors has spent no processing time, and a screen that
 * said "No traffic yet" three rows above "496,571 ms" would be caught by the
 * first person who read both.
 */
export function usageExtras(
  projectId: string,
  requests: number,
): { cpuMs: number; subRequests: number } {
  if (requests <= 0) return { cpuMs: 0, subRequests: 0 };
  const next = roll(projectId);
  return {
    cpuMs: Math.round(requests * (4 + next() * 8)),
    subRequests: Math.round(requests * (0.2 + next() * 0.5)),
  };
}

/* ------------------------------- the traffic screen (observability WIP) */

/**
 * Traffic is a derived view, not a key in DemoData, and that is deliberate.
 *
 * Everything else in the world is a record somebody could have written down: a
 * deploy happened, a key was added. A traffic window is not. It is a question
 * asked about a moment, and its answer moves every hour, so a copy of it
 * sitting in a persisted store would go stale the way a printed timetable
 * does. `liveLogLines` and `dailyCounts` above are the same shape of thing for
 * the same reason: seeded functions rather than seeded state.
 *
 * The rest of this section is one derivation with three rules it must hold.
 *
 * RULE ONE, the non negotiable one. Health.requestsLastHour and
 * Health.errorsLastHour drive the overview lamp. The last bucket of the 24h
 * series IS those two numbers, pinned, not a second estimate that happens to
 * land nearby. If the overview says 640 requests in the last hour and this
 * chart implies something else, a person who notices stops trusting both
 * screens, and the lamp is the one thing in this product that cannot afford to
 * be doubted.
 *
 * RULE TWO. The thirty day total is the requests number the usage screen is
 * already printing, because it is handed out of that number rather than made
 * up beside it. Same move as fileBytesOf in the sam scenario and usageExtras
 * above: derive from the fixture that already exists so the two can never
 * drift, including when somebody edits one of them next month.
 *
 * RULE THREE. TrafficOutcome.crashed is the error groups, counted. Not a
 * number that looks like them. The two come off one pipeline in the real
 * product (one ingest, two consumers: the activity screen gets the groups,
 * this screen gets the counts), so they are built here off one fixture too,
 * and a reviewer can add up the error list and get the crash count.
 */

/** The three windows, in the order the picker offers them. */
export const TRAFFIC_WINDOWS: TrafficWindow[] = ['24h', '7d', '30d'];

/** Days per window. 24h is one day, drawn as hours. */
export const TRAFFIC_WINDOW_DAYS: Record<TrafficWindow, number> = {
  '24h': 1,
  '7d': 7,
  '30d': 30,
};

/**
 * Above this many visits in a window the platform stops counting every request
 * and counts a sample instead, and `estimated` goes true.
 *
 * The real threshold is the platform's and it is not this number. This one only
 * has to make the state reachable in the demo, which it does: Herb and Ash is
 * under it for a day and a week and over it for a month, so changing the window
 * changes the copy from a count to "About 42,000 visits" and a reviewer can
 * read both sentences without editing a fixture to get there.
 */
const SAMPLED_ABOVE = 25_000;

/**
 * How many days of addresses and countries the platform keeps, by plan.
 *
 * Shorter than the visits window on purpose, and the screen says so. Reading a
 * three day list as though it covered thirty is the specific mistake this
 * number exists to prevent, which is why it goes on the wire as a field on the
 * family rather than sitting in the screen as a constant.
 */
function keptForDays(plan: Plan): number {
  return plan === 'basic' ? 3 : 7;
}

/** The span the fixture rows below were written against. */
const FIXTURE_PATH_DAYS = 3;

/**
 * The shape of one day, hour by hour, in local time.
 *
 * It is here so the 24h chart reads like a day rather than like noise: dead at
 * four in the morning, busy at eleven. Over the daily windows it washes out and
 * only the per hour jitter survives, which is correct, because a bar labelled
 * Tuesday should not carry a shape nobody can see.
 *
 * The night really is close to nothing. That is what makes a quiet app have
 * hours with no visits in them at all, which is the only way TrafficSpeed's
 * per bucket null is reachable in the demo: an hour nothing arrived in has
 * nothing to time, and the screen has to be able to draw that hole.
 */
const DIURNAL = [
  0.12, 0.07, 0.05, 0.05, 0.08, 0.22, 0.55, 1.0, 1.35, 1.55, 1.6, 1.55,
  1.5, 1.55, 1.6, 1.55, 1.45, 1.4, 1.35, 1.25, 1.1, 0.85, 0.5, 0.25,
];

/**
 * What the platform is measuring for one project, plus the two families only a
 * response reading pipeline can answer.
 *
 * `measured` is a fixture fact rather than a rule derived from dates, for the
 * same reason ErrorDetail.ownFrames is one: the real answer is whether that
 * project's worker has the pipeline switched on, the platform knows it, and a
 * screen that guessed it from a deploy date would be wrong the first time
 * somebody redeployed an old project.
 *
 * It is also the whole demonstration of why the null rule earns its keep. Two
 * projects on one account, the same screen, different amounts of the page
 * filled in, and the page says which is which instead of drawing two identical
 * charts of zeros.
 */
interface TrafficFixture {
  /** False when the response reading pipeline has never run for this project. */
  measured: boolean;
  /** Visits this window over visits the window before. Above 1 is growth. */
  growth: number;
  medianMs: number;
  slowestTenthMs: number;
  /**
   * When the app started answering with errors it had caught itself, and how
   * many of its visits get one. Crashes are not here: those are counted off
   * the error groups, per RULE THREE, so this is only the half of the error
   * count that never reaches the error list.
   */
  caughtSince: number | null;
  caughtShare: number;
  /** Shares of visits, as fractions. Answered is whatever is left over. */
  sentElsewhere: number;
  notFound: number;
  leftEarly: number;
  /** Written against FIXTURE_PATH_DAYS days and scaled to the window asked for. */
  paths: TrafficPath[];
  places: TrafficPlace[];
}

/**
 * A project whose response reading pipeline has never run.
 *
 * VISITS ARE NOT PART OF THIS. Counting requests is the cheap family, it comes
 * off a dataset every project is already in, and it keeps working here: what
 * this record turns off is the four families that need somebody to have read
 * the responses, so outcome, speed, pages and places all answer null while the
 * chart above them is full. That combination is the honest first shippable
 * state of the whole screen, and it is the state a project that arrived from
 * somewhere else lands in: the page renders, says what it does not know yet,
 * and does not invent the rest.
 */
const NOT_MEASURED: TrafficFixture = {
  measured: false,
  growth: 1,
  medianMs: 0,
  slowestTenthMs: 0,
  caughtSince: null,
  caughtShare: 0,
  sentElsewhere: 0,
  notFound: 0,
  leftEarly: 0,
  paths: [],
  places: [],
};

/**
 * The four moods, one per project, so the demo can reach all of them.
 *
 * herb-and-ash    healthy and busy. Every family filled in, no errors at all,
 *                 and enough visits over a month to trip the estimate copy.
 * studio-ledger   the recent error spike. The caught errors start three days
 *                 ago, which is when b8f2a06 deployed, so the step in the
 *                 errors line lands on a deploy mark. That is the one thing
 *                 this screen can teach that no other screen can.
 * wedding-rsvp    paused. Measured, and nothing there: outcome is six zeros
 *                 and both lists are empty arrays, which is the "we looked and
 *                 there was nothing" sentence rather than the "we do not
 *                 measure this yet" one.
 * soi-cats        arrived from Loop and has never deployed here, so the
 *                 pipeline has never run for it: its visits are counted and
 *                 drawn, and the four families that need a response read
 *                 (outcome, speed, pages, places) all answer null. That is the
 *                 staged variant, and it is reachable on this project with no
 *                 state parameter, which is what the contract promises.
 */
const TRAFFIC_FIXTURES: Record<string, TrafficFixture> = {
  'herb-and-ash': {
    measured: true,
    growth: 1.18,
    medianMs: 28,
    slowestTenthMs: 142,
    caughtSince: null,
    caughtShare: 0,
    sentElsewhere: 0.031,
    notFound: 0.006,
    leftEarly: 0.004,
    // Two of these rows are the reason the list is worth reading at all.
    // /wp-admin is a stranger checking whether this is a WordPress site, which
    // is ordinary and needs nothing done about it. /recipes/tom-kha is
    // somebody's own older link that stopped resolving when the recipe was
    // renamed, which is not ordinary and is a five minute fix. Both are pages
    // that are not there, and only one of them is the person's problem.
    paths: [
      { path: '/recipes', method: 'GET', visits: 1_380, notFound: 0, errored: 0 },
      { path: '/', method: 'GET', visits: 820, notFound: 0, errored: 0 },
      { path: '/images/leek-stew.jpg', method: 'GET', visits: 742, notFound: 0, errored: 0 },
      { path: '/recipes/overnight-focaccia', method: 'GET', visits: 411, notFound: 0, errored: 0 },
      {
        path: '/recipes/charred-leek-and-butter-bean-stew',
        method: 'GET',
        visits: 388,
        notFound: 0,
        errored: 0,
      },
      { path: '/search', method: 'GET', visits: 180, notFound: 0, errored: 0 },
      { path: '/saved', method: 'POST', visits: 60, notFound: 0, errored: 0 },
      { path: '/wp-admin', method: 'GET', visits: 47, notFound: 47, errored: 0 },
      { path: '/recipes/tom-kha', method: 'GET', visits: 31, notFound: 31, errored: 0 },
    ],
    // The tail rows carry a null rather than a zero. At seventeen visits the
    // platform cannot say how many of them errored without guessing, so it says
    // nothing, which is the same rule as the families one level up.
    places: [
      { country: 'Thailand', visits: 2_411, errors: 0 },
      { country: 'United Kingdom', visits: 704, errors: 0 },
      { country: 'United States', visits: 512, errors: 0 },
      { country: 'Singapore', visits: 188, errors: 0 },
      { country: 'Australia', visits: 96, errors: null },
      { country: 'Germany', visits: 41, errors: null },
      { country: 'Japan', visits: 17, errors: null },
    ],
  },

  'studio-ledger': {
    measured: true,
    // Down a little on the month before, which is a real thing to be told and
    // is not a failure. The screen states it and does not scold anybody.
    growth: 0.94,
    medianMs: 36,
    slowestTenthMs: 268,
    // Three days, which is when b8f2a06 shipped and when err_invoice_total was
    // first seen. One number, and the two fixtures that already mention that
    // moment agree with it.
    caughtSince: 3 * DAY,
    caughtShare: 0.05,
    sentElsewhere: 0.014,
    notFound: 0.009,
    leftEarly: 0.006,
    paths: [
      { path: '/invoices', method: 'GET', visits: 121, notFound: 0, errored: 0 },
      { path: '/clients', method: 'GET', visits: 58, notFound: 0, errored: 0 },
      { path: '/invoices/481', method: 'GET', visits: 34, notFound: 0, errored: 0 },
      { path: '/clients/7/invoices', method: 'GET', visits: 21, notFound: 0, errored: 6 },
      { path: '/invoices/481/paid', method: 'POST', visits: 12, notFound: 0, errored: 0 },
      { path: '/invoices/482', method: 'GET', visits: 9, notFound: 0, errored: 9 },
      { path: '/invoices/9001', method: 'GET', visits: 7, notFound: 7, errored: 0 },
    ],
    places: [
      { country: 'Thailand', visits: 208, errors: 19 },
      { country: 'Singapore', visits: 54, errors: 5 },
      { country: 'United States', visits: 22, errors: null },
      { country: 'Germany', visits: 6, errors: null },
    ],
  },

  // Paused, and measured. Every family answers, and every answer is empty.
  // Read this record next to soi-cats below: on the wire they look nothing
  // alike, which is the entire point, because in every other product on the
  // market they look identical.
  'wedding-rsvp': {
    measured: true,
    growth: 1,
    medianMs: 0,
    slowestTenthMs: 0,
    caughtSince: null,
    caughtShare: 0,
    sentElsewhere: 0,
    notFound: 0,
    leftEarly: 0,
    paths: [],
    places: [],
  },

  'soi-cats': NOT_MEASURED,
};

const HOUR_MS = 60 * 60 * 1000;

/**
 * One hour of the backbone.
 *
 * Crashes and caught errors are carried apart all the way through, and only
 * added together when a TrafficPoint is built. Merging them earlier is how a
 * product ends up with one "error rate" that is true of neither measurement.
 */
interface TrafficHour {
  t: number;
  visits: number;
  crashed: number;
  caught: number;
}

function hourStart(ms: number): number {
  return Math.floor(ms / HOUR_MS) * HOUR_MS;
}

/** Local midnight, n days ago. A negative n is a midnight still to come. */
function dayStart(daysAgo: number): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - daysAgo);
  return d.getTime();
}

/**
 * The request count behind the compute meter this project is already showing.
 * Off `advanced`, not off the metric: the metric is compute units now, and a
 * traffic chart built from units would claim visits nobody made.
 */
function requestsUsed(usage: Usage | null): number {
  return usage?.advanced?.requests ?? 0;
}

/**
 * Hand a total out across a run of hours, weighted by the shape of the day plus
 * a deterministic jitter.
 *
 * The fraction that does not fit an hour is carried into the next one rather
 * than rounded away, which is what stops a rate of a fifth of a visit an hour
 * from flattening into a row of zeros with the whole total dumped on the end.
 */
function spread(hours: TrafficHour[], total: number, salt: string): void {
  if (hours.length === 0 || total <= 0) return;
  const next = roll(salt);
  const weights = hours.map((h) => DIURNAL[new Date(h.t).getHours()] * (0.7 + next() * 0.6));
  const sum = weights.reduce((a, w) => a + w, 0);
  let carry = 0;
  let placed = 0;
  hours.forEach((hour, i) => {
    carry += (weights[i] / sum) * total;
    const whole = Math.min(Math.floor(carry), total - placed);
    hour.visits = whole;
    carry -= whole;
    placed += whole;
  });
  hours[hours.length - 1].visits += total - placed;
}

/**
 * Count the error groups into the hours they happened in. RULE THREE.
 *
 * A group knows how many times it happened and between which two moments, so
 * the occurrences go across that span and nowhere else. A group that is marked
 * resolved still counts: resolved means somebody fixed it, not that it never
 * happened, and the chart is a record of what the app did.
 */
function spreadCrashes(hours: TrafficHour[], groups: ErrorGroup[]): void {
  for (const group of groups) {
    const from = hourStart(Date.parse(group.firstSeen));
    const to = hourStart(Date.parse(group.lastSeen));
    const run = hours.filter((h) => h.t >= from && h.t <= to);
    if (run.length === 0) continue;
    const next = roll(group.id);
    const weights = run.map(() => 0.4 + next());
    const sum = weights.reduce((a, w) => a + w, 0);
    let carry = 0;
    let placed = 0;
    run.forEach((hour, i) => {
      carry += (weights[i] / sum) * group.count;
      const whole = Math.min(Math.floor(carry), group.count - placed);
      hour.crashed += whole;
      carry -= whole;
      placed += whole;
    });
    run[run.length - 1].crashed += group.count - placed;
  }
}

/**
 * Sixty days of hours, in two halves that meet at a midnight.
 *
 * The recent half covers exactly the thirty calendar days the 30d window draws
 * and sums to the usage screen's requests number. The older half is the same
 * span before it and exists only so `previousVisits` has something honest to
 * be, which is why nothing ever reads it as a series.
 */
function backbone(
  project: Project,
  usage: Usage | null,
  groups: ErrorGroup[],
  fx: TrafficFixture,
): TrafficHour[] {
  const now = hourStart(Date.now());
  const split = dayStart(TRAFFIC_WINDOW_DAYS['30d'] - 1);
  const from = dayStart(TRAFFIC_WINDOW_DAYS['30d'] * 2 - 1);

  const hours: TrafficHour[] = [];
  for (let t = from; t <= now; t += HOUR_MS) hours.push({ t, visits: 0, crashed: 0, caught: 0 });
  if (hours.length === 0) return hours;

  const total = requestsUsed(usage);
  const before = hours.filter((h) => h.t < split);
  const recent = hours.filter((h) => h.t >= split);

  spread(before, Math.round(total / (fx.growth || 1)), `${project.id}:before`);

  // The pinned hour is held out of the distribution and then set, so the thirty
  // day total still lands on the usage number to the request. RULE ONE and
  // RULE TWO at the same time, which is the only way both can be true.
  const pinned = project.health?.requestsLastHour ?? 0;
  spread(recent.slice(0, -1), Math.max(0, total - pinned), `${project.id}:recent`);
  if (recent.length > 0) recent[recent.length - 1].visits = pinned;

  spreadCrashes(hours, groups);

  // Caught errors are a share of visits, and they only exist after the app
  // started answering with them. They never reach the error list, which is the
  // whole reason the screen prints two numbers and a sentence about why.
  if (fx.caughtSince !== null && fx.caughtShare > 0) {
    const since = Date.now() - fx.caughtSince;
    let carry = 0;
    for (const hour of hours) {
      if (hour.t < since) continue;
      carry += hour.visits * fx.caughtShare;
      const whole = Math.floor(carry);
      hour.caught = whole;
      carry -= whole;
    }
  }

  // The lamp is the authority on the hour a person is standing in. RULE ONE.
  const last = hours[hours.length - 1];
  last.crashed = project.health?.errorsLastHour ?? 0;
  last.caught = 0;

  // An hour cannot have more errors in it than it had visits.
  for (const hour of hours) {
    const room = Math.max(0, hour.visits - hour.crashed);
    hour.crashed = Math.min(hour.crashed, hour.visits);
    hour.caught = Math.min(hour.caught, room);
  }

  return hours;
}

/** Sum a run of hours into one bucket per calendar day, oldest first. */
function intoDays(hours: TrafficHour[], days: number, endingDaysAgo: number): TrafficHour[] {
  const out: TrafficHour[] = [];
  for (let k = days - 1; k >= 0; k -= 1) {
    const from = dayStart(k + endingDaysAgo);
    const to = dayStart(k + endingDaysAgo - 1);
    const day: TrafficHour = { t: from, visits: 0, crashed: 0, caught: 0 };
    for (const hour of hours) {
      if (hour.t >= from && hour.t < to) {
        day.visits += hour.visits;
        day.crashed += hour.crashed;
        day.caught += hour.caught;
      }
    }
    out.push(day);
  }
  return out;
}

function point(hour: TrafficHour): TrafficPoint {
  return {
    at: new Date(hour.t).toISOString(),
    visits: hour.visits,
    // The one place the two error counts are added together, and even here
    // they are added for a line on a chart, never for a headline number.
    errors: hour.crashed + hour.caught,
  };
}

/**
 * The six buckets, disjoint, summing to visits.
 *
 * answered takes the remainder rather than a share of its own, which is what
 * makes the sum exact instead of nearly exact. A screen that prints six numbers
 * and a total has to survive somebody adding them up.
 */
function outcomeOf(
  visits: number,
  crashed: number,
  caught: number,
  fx: TrafficFixture,
): TrafficOutcome | null {
  if (!fx.measured) return null;
  const sentElsewhere = Math.round(visits * fx.sentElsewhere);
  const notFound = Math.round(visits * fx.notFound);
  const leftEarly = Math.round(visits * fx.leftEarly);
  const answered = Math.max(
    0,
    visits - sentElsewhere - notFound - leftEarly - caught - crashed,
  );
  return { answered, sentElsewhere, notFound, errored: caught, crashed, leftEarly };
}

function speedOf(
  series: TrafficPoint[],
  visits: number,
  fx: TrafficFixture,
  salt: string,
): TrafficSpeed | null {
  if (!fx.measured || visits <= 0) return null;
  const next = roll(`${salt}:speed`);
  return {
    medianMs: fx.medianMs,
    slowestTenthMs: fx.slowestTenthMs,
    // A bucket nothing arrived in has nothing to time, so it is null and not
    // zero. A zero would draw a floor the app never touched.
    byDay: series.map((p) =>
      p.visits <= 0 ? null : Math.round(fx.medianMs * (0.85 + next() * 0.3)),
    ),
  };
}

/**
 * How much of the fixture rows this window gets to see, clamped at what the
 * platform still holds.
 *
 * A 24h window shows a day of addresses; a 7d or 30d window shows all three
 * days that are left, and says so. This is where two windows on one screen
 * stop being a caveat somebody has to remember and become a number the screen
 * can print.
 */
function coverage(window: TrafficWindow, kept: number): number {
  return Math.min(TRAFFIC_WINDOW_DAYS[window], kept) / FIXTURE_PATH_DAYS;
}

function pathsOf(fx: TrafficFixture, window: TrafficWindow, plan: Plan): Traffic['paths'] {
  if (!fx.measured) return null;
  const kept = keptForDays(plan);
  const scale = coverage(window, kept);
  return {
    keptForDays: kept,
    rows: fx.paths
      .map((row) => ({
        ...row,
        visits: Math.round(row.visits * scale),
        notFound: Math.round(row.notFound * scale),
        errored: Math.round(row.errored * scale),
      }))
      .filter((row) => row.visits > 0)
      .sort((a, b) => b.visits - a.visits),
  };
}

function placesOf(fx: TrafficFixture, window: TrafficWindow, plan: Plan): Traffic['places'] {
  if (!fx.measured) return null;
  const kept = keptForDays(plan);
  const scale = coverage(window, kept);
  return {
    keptForDays: kept,
    rows: fx.places
      .map((row) => ({
        country: row.country,
        visits: Math.round(row.visits * scale),
        // null stays null. A row the platform could not break down at full
        // volume does not become answerable at a third of it.
        errors: row.errors === null ? null : Math.round(row.errors * scale),
      }))
      .filter((row) => row.visits > 0)
      .sort((a, b) => b.visits - a.visits),
  };
}

/**
 * One window of traffic for one project, built out of the fixtures the rest of
 * the world is already made of.
 *
 * Pure, and it is handed everything it reads rather than reaching into the
 * store, so the mock accessor stays a one line read and the derivation can be
 * checked on its own.
 */
export function trafficFor(
  project: Project,
  deploys: Deploy[],
  groups: ErrorGroup[],
  usage: Usage | null,
  window: TrafficWindow,
): Traffic {
  const fx = TRAFFIC_FIXTURES[project.slug] ?? NOT_MEASURED;
  const plan = usage?.plan ?? 'basic';
  const hours = backbone(project, usage, groups, fx);
  const days = TRAFFIC_WINDOW_DAYS[window];

  const current = window === '24h' ? hours.slice(-24) : intoDays(hours, days, 0);
  const earlier = window === '24h' ? hours.slice(-48, -24) : intoDays(hours, days, days);

  const series = current.map(point);
  const visits = current.reduce((sum, h) => sum + h.visits, 0);
  const crashed = current.reduce((sum, h) => sum + h.crashed, 0);
  const caught = current.reduce((sum, h) => sum + h.caught, 0);

  // Younger than two windows means there is no before to compare against, and
  // "up from 0" would be a compliment nobody earned. null, never zero.
  const age = Date.now() - Date.parse(project.createdAt);
  const hasBefore = age >= 2 * days * DAY;

  const from = window === '24h' ? Date.now() - DAY : dayStart(days - 1);

  return {
    projectId: project.id,
    window,
    estimated: visits > SAMPLED_ABOVE,
    visits,
    previousVisits: hasBefore ? earlier.reduce((sum, h) => sum + h.visits, 0) : null,
    series,
    marks: deploys
      .filter((deploy) => Date.parse(deploy.createdAt) >= from)
      .filter((deploy) => !['deploying', 'queued', 'skipped'].includes(deploy.status))
      .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
      .map((deploy) => ({
        at: deploy.createdAt,
        deployId: deploy.id,
        version: deploy.commitSha.slice(0, 7),
        failed: deploy.status === 'didnt-work',
      })),
    outcome: outcomeOf(visits, crashed, caught, fx),
    speed: speedOf(series, visits, fx, `${project.id}:${window}`),
    paths: pathsOf(fx, window, plan),
    places: placesOf(fx, window, plan),
  };
}

export const NEW_PROJECT_DRAFT: NewProjectDraft = {
  name: 'Herb and Ash',
  slug: 'herb-and-ash',
  webAddress: `https://herb-and-ash.${PUBLIC_DOMAIN}`,
  repo: { provider: 'github', owner: 'sam', name: 'herb-and-ash', branch: 'main', connected: true },
  commitSha: '0c5e7b8',
  commitMessage: 'First deploy',
  buildFailure: {
    location: 'src/app.ts line 42',
    fixRequest: `My app is hosted on SV Cloud. A deploy just failed and I need you to fix it.

Project: Herb and Ash
Deploy: 0c5e7b8 "First deploy"
Build step that failed: Connecting all the wires

The build error:

  src/app.ts(42,10): error TS2551: Property 'formatServings' does not exist
  on type 'typeof import("/src/lib/recipes")'. Did you mean 'formatServing'?

    40 |   const rows = await listRecipes(db, tag);
    41 |   const total = rows.reduce((a, r) => a + r.servings, 0);
  > 42 |   return recipes.formatServings(total);
       |          ^^^^^^^^^^^^^^^^^^^^^^
    43 | }

Please find the cause, fix it in my code, and commit the fix to main. Pushing
to main deploys automatically, so do not run any deploy commands.`,
  },
};

/* --------------------------------------------------------------- uploads */

/**
 * The files S13's upload states drop, screen spec S13 "upload in progress"
 * and "upload fails (one of three)".
 *
 * Three trays rather than one, because each one teaches a different thing and
 * a state a reviewer cannot open alone is a state nobody reviews:
 *
 *   UPLOAD_DEMO          three files at staggered speeds, all three land
 *   UPLOAD_DEMO_STOPPED  one transfer stops partway and the other two land.
 *                        PARTIAL FAILURE IS THE HONEST CASE: a tray where
 *                        everything fails at once is a shape a real upload
 *                        almost never has, and it hides the question the
 *                        screen has to answer, which is "which ones made it"
 *   UPLOAD_DEMO_REFUSED  one file over the size cap and one kind of file that
 *                        file storage does not hold. Both are refused before a
 *                        byte moves, so neither one ever shows a progress
 *                        track, and neither one is offered Try again
 *
 * The names are Herb and Ash's: a recipe photograph, a phone photograph from a
 * market, a spreadsheet of what is in the pantry, and one installer that a
 * person dragged in from their downloads folder by accident, which is how that
 * refusal actually happens.
 */
export const UPLOAD_DEMO: UploadSeedItem[] = [
  {
    name: 'cardamom-buns.jpg',
    bytes: 738_304,
    contentType: 'image/jpeg',
    durationMs: 5_600,
    refusal: null,
  },
  {
    name: 'proofing-basket.jpg',
    bytes: 1_142_784,
    contentType: 'image/jpeg',
    durationMs: 8_400,
    refusal: null,
  },
  {
    name: 'pantry-august.csv',
    bytes: 4_608,
    contentType: 'text/csv',
    durationMs: 2_200,
    refusal: null,
  },
];

export const UPLOAD_DEMO_STOPPED: UploadSeedItem[] = [
  {
    name: 'cardamom-buns.jpg',
    bytes: 738_304,
    contentType: 'image/jpeg',
    durationMs: 5_600,
    refusal: null,
  },
  {
    name: 'sourdough-timeline.png',
    bytes: 3_355_443,
    contentType: 'image/png',
    durationMs: 9_200,
    refusal: 'stopped',
  },
  {
    name: 'pantry-august.csv',
    bytes: 4_608,
    contentType: 'text/csv',
    durationMs: 2_200,
    refusal: null,
  },
];

export const UPLOAD_DEMO_REFUSED: UploadSeedItem[] = [
  {
    name: 'cardamom-buns.jpg',
    bytes: 738_304,
    contentType: 'image/jpeg',
    durationMs: 5_600,
    refusal: null,
  },
  {
    name: 'market-haul-raw.tiff',
    bytes: 41_418_752,
    contentType: 'image/tiff',
    durationMs: 0,
    refusal: 'too-big',
  },
  {
    name: 'photo-editor-setup.dmg',
    bytes: 268_435_456,
    contentType: 'application/x-apple-diskimage',
    durationMs: 0,
    refusal: 'wrong-type',
  },
];

/* ----------------------------------------------------- the database rows */

/**
 * The rows behind the six Herb and Ash tables and the four Studio Ledger
 * tables, screen spec 5.3 and 5.4.
 *
 * TWO RULES GOVERN THIS BLOCK, and both exist so the grid can be believed.
 *
 * First, the gateway caps a page at 100 rows, so a table never seeds more
 * than 100 of them however many it reports. That is not a shortcut: it is the
 * condition S11's footer has to render as normal rather than as a fault, and
 * seeding 1,204 rows to then throw 1,104 away would be inventing the exact
 * state the cap exists to prevent.
 *
 * Second, every value is deterministic. Nothing here calls Math.random, so a
 * screenshot taken today and a screenshot taken in November show the same
 * cells, and a reviewer comparing two artboards is comparing the layout
 * rather than the data.
 *
 * The first twelve recipes are the spec's, verbatim, including the two rows
 * that carry a NULL cover image and is_public false. Those two rows are the
 * whole reason the grid has a NULL rendering and a boolean rendering to
 * design, so they are not decoration and they must not be tidied away.
 */

/** Timestamps are literals a database returned, not moments to age. */
function dbStamp(base: number, msFrom: number): string {
  const date = new Date(base + msFrom);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}` +
    ` ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
  );
}

function rowsOf(table: string, cells: Record<string, DbCell>[]): DbRow[] {
  return cells.map((one, index) => ({ id: `${table}-${index + 1}`, cells: one }));
}

/** The plan's page cap, stated once. S11 renders it as a normal condition. */
export const ROW_CAP = 100;

const RECIPE_BASE = Date.UTC(2026, 6, 15, 8, 0);
const SAVED_BASE = Date.UTC(2026, 7, 14, 9, 0);
const USER_BASE = Date.UTC(2026, 5, 5, 10, 0);
const LEDGER_BASE = Date.UTC(2026, 6, 2, 9, 0);

interface RecipeSeed {
  title: string;
  servings: number;
  minutes: number;
  isPublic: boolean;
  image: string | null;
  at: string;
}

const RECIPES_HEAD: RecipeSeed[] = [
  { title: 'Charred leek and butter bean stew', servings: 4, minutes: 45, isPublic: true, image: '/images/leek-stew.jpg', at: '2026-06-04 09:14' },
  { title: 'Green mango salad with peanuts', servings: 2, minutes: 20, isPublic: true, image: '/images/green-mango.jpg', at: '2026-06-04 09:31' },
  { title: 'Overnight focaccia', servings: 8, minutes: 720, isPublic: true, image: '/images/focaccia-crumb.jpg', at: '2026-06-07 18:02' },
  { title: 'Tom kha with roasted squash', servings: 4, minutes: 40, isPublic: true, image: '/images/tom-kha.jpg', at: '2026-06-09 12:44' },
  { title: 'Ash-baked sweet potato', servings: 2, minutes: 60, isPublic: true, image: '/images/sweet-potato.jpg', at: '2026-06-11 19:20' },
  { title: 'Lemongrass iced tea', servings: 6, minutes: 15, isPublic: true, image: '/images/iced-tea.jpg', at: '2026-06-12 08:05' },
  { title: 'Burnt butter sage gnocchi', servings: 3, minutes: 35, isPublic: true, image: '/images/gnocchi.jpg', at: '2026-06-18 20:11' },
  { title: 'Pandan coconut custard', servings: 8, minutes: 90, isPublic: false, image: null, at: '2026-06-22 15:37' },
  { title: 'Charred cabbage with anchovy butter', servings: 4, minutes: 30, isPublic: true, image: '/images/cabbage.jpg', at: '2026-07-01 11:09' },
  { title: 'Cold soba with sesame', servings: 2, minutes: 18, isPublic: true, image: '/images/soba.jpg', at: '2026-07-04 13:52' },
  { title: 'Sourdough discard crackers', servings: 12, minutes: 45, isPublic: true, image: '/images/crackers.jpg', at: '2026-07-09 16:28' },
  { title: 'Smoked eggplant dip', servings: 6, minutes: 50, isPublic: false, image: null, at: '2026-07-14 10:03' },
];

const METHODS = [
  'Charred', 'Ash-baked', 'Smoked', 'Overnight', 'Cold', 'Roasted',
  'Braised', 'Grilled', 'Steamed', 'Blistered', 'Slow-cooked', 'Pan-fried',
];

const SUBJECTS = [
  'leeks with lemon', 'cabbage with anchovy butter', 'sweet potato with lime',
  'squash with chilli', 'aubergine with miso', 'tomatoes with basil',
  'carrots with cumin', 'mushrooms with thyme', 'fennel with orange',
  'peppers with feta', 'courgettes with mint', 'beetroot with dill',
  'cauliflower with tahini', 'broccoli with garlic', 'potatoes with rosemary',
  'pumpkin with sage', 'corn with lime', 'green beans with almonds',
  'shallots with vinegar', 'celeriac with cream',
];

function slugOf(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/**
 * Rows thirteen to one hundred, composed from a method and a subject so the
 * column reads like a recipe site rather than like padding. The pairing walks
 * both lists at different rates, so no title repeats inside the page.
 */
function recipeAt(index: number): RecipeSeed {
  // The subject walk starts one place in, so no composed title can land on
  // one of the twelve the spec wrote by hand.
  const subject = SUBJECTS[(Math.floor(index / METHODS.length) + 1) % SUBJECTS.length];
  const title = `${METHODS[index % METHODS.length]} ${subject}`;
  const isPublic = index % 9 !== 3;
  return {
    title,
    servings: [2, 4, 6, 8][index % 4],
    minutes: [18, 25, 30, 35, 40, 55, 70, 90][index % 8],
    isPublic,
    image: isPublic ? `/images/${slugOf(title)}.jpg` : null,
    at: dbStamp(RECIPE_BASE, index * 7 * 60 * 60 * 1000),
  };
}

const RECIPE_SEEDS: RecipeSeed[] = [
  ...RECIPES_HEAD,
  ...Array.from({ length: ROW_CAP - RECIPES_HEAD.length }, (_, i) => recipeAt(RECIPES_HEAD.length + i)),
];

const RECIPE_ROWS: DbRow[] = rowsOf(
  'recipes',
  RECIPE_SEEDS.map((one, index) => ({
    id: index + 1,
    title: one.title,
    servings: one.servings,
    minutes: one.minutes,
    is_public: one.isPublic,
    cover_image: one.image,
    created_at: one.at,
  })),
);

const PEOPLE = [
  'Anna Bergstrom', 'Marco Villa', 'Priya Nair', 'Tom Beck', 'Lena Fischer',
  'Yusuf Demir', 'Mia Halvorsen', 'Diego Rojas', 'Nok Chaiwat', 'Sara Lindqvist',
  'Owen Blake', 'Hana Kim', 'Ravi Menon', 'Elena Costa', 'Jonas Weber',
  'Aisha Rahman', 'Pete Fallon', 'Mei Chen', 'Luca Ferrari', 'Grace Adeyemi',
  'Ben Carter', 'Ploy Srisai',
];

const USER_ROWS: DbRow[] = rowsOf(
  'users',
  PEOPLE.map((name, index) => ({
    id: index + 1,
    email: `${name.toLowerCase().replace(/\s+/g, '.')}@example.com`,
    name,
    created_at: dbStamp(USER_BASE, index * 30 * 60 * 60 * 1000),
  })),
);

/** Thirty six saves, spread across the readers who actually come back. */
const SAVED_RECIPE_ROWS: DbRow[] = rowsOf(
  'saved_recipes',
  Array.from({ length: 36 }, (_, index) => ({
    id: index + 1,
    user_id: ((index * 5) % PEOPLE.length) + 1,
    recipe_id: ((index * 7) % ROW_CAP) + 1,
    created_at: dbStamp(SAVED_BASE, index * 3.5 * 60 * 60 * 1000),
  })),
);

const INGREDIENT_NAMES = [
  'sea salt', 'black pepper', 'olive oil', 'garlic', 'lemon', 'butter',
  'plain flour', 'caster sugar', 'eggs', 'whole milk', 'double cream',
  'parmesan', 'basil', 'parsley', 'coriander', 'cumin', 'paprika',
  'chilli flakes', 'soy sauce', 'fish sauce', 'rice vinegar', 'sesame oil',
  'ginger', 'lemongrass', 'coconut milk', 'palm sugar', 'tamarind paste',
  'peanuts', 'white rice', 'rice noodles', 'yeast', 'honey', 'thyme',
  'rosemary', 'bay leaf', 'tomato paste', 'red onion', 'shallot',
  'spring onion', 'lime',
];

const INGREDIENT_QUALIFIERS = ['smoked', 'dried', 'toasted'];
const UNITS = ['g', 'ml', 'tsp', 'tbsp', 'piece', 'clove', 'bunch'];

const INGREDIENT_ROWS: DbRow[] = rowsOf(
  'ingredients',
  Array.from({ length: ROW_CAP }, (_, index) => {
    const base = INGREDIENT_NAMES[index % INGREDIENT_NAMES.length];
    const round = Math.floor(index / INGREDIENT_NAMES.length);
    return {
      id: index + 1,
      name: round === 0 ? base : `${INGREDIENT_QUALIFIERS[(round - 1) % INGREDIENT_QUALIFIERS.length]} ${base}`,
      unit: UNITS[index % UNITS.length],
    };
  }),
);

const RECIPE_INGREDIENT_ROWS: DbRow[] = rowsOf(
  'recipe_ingredients',
  Array.from({ length: ROW_CAP }, (_, index) => ({
    id: index + 1,
    recipe_id: Math.floor(index / 9) + 1,
    ingredient_id: ((index * 11) % ROW_CAP) + 1,
    quantity: [1, 2, 4, 5, 10, 15, 20, 30, 60, 120][index % 10],
  })),
);

const MIGRATION_ROWS: DbRow[] = rowsOf('_sv_migrations', [
  { id: 1, name: '0001_create_recipes', applied_at: '2026-06-02 11:04' },
  { id: 2, name: '0002_create_ingredients', applied_at: '2026-06-02 11:04' },
  { id: 3, name: '0003_create_users', applied_at: '2026-06-06 09:22' },
  { id: 4, name: '0004_create_saved_recipes', applied_at: '2026-08-20 08:41' },
]);

const CLIENT_NAMES = [
  'Baan Studio', 'North Ridge Coffee', 'Salt and Pine', 'Harbourline Books',
  'Kiln and Co', 'Foxglove Florist', 'Third Street Dental', 'Marlow Bicycles',
  'Ridgeline Ceramics', 'Verity Legal', 'Blue Hour Photo', 'Tanglin Tailors',
  'Riverbed Yoga', 'Copperleaf Bakery', 'Studio Nine', 'Pinewood Vets',
  'Aster Interiors', 'Longtail Travel', 'Quiet Machines', 'Meridian Audio',
  'Fern and Thistle', 'Orchard Lane Nursery', 'Halcyon Print', 'Sable Fitness',
  'Two Rivers Brewing', 'Lantern Theatre', 'Wayfare Outfitters', 'Ash and Oak',
];

const CLIENT_ROWS: DbRow[] = rowsOf(
  'clients',
  CLIENT_NAMES.map((name, index) => ({
    id: index + 1,
    name,
    email: `hello@${slugOf(name)}.com`,
  })),
);

const INVOICE_ROWS: DbRow[] = rowsOf(
  'invoices',
  Array.from({ length: ROW_CAP }, (_, index) => ({
    id: index + 1,
    client_id: (index % CLIENT_NAMES.length) + 1,
    total: 4500 + ((index * 1350) % 58_000),
    paid: index % 4 !== 1,
    issued_at: dbStamp(LEDGER_BASE, index * 11 * 60 * 60 * 1000),
  })),
);

const LINE_ITEM_LABELS = [
  'Design retainer', 'Logo revisions', 'Brand guidelines', 'Print setup',
  'Photography day', 'Illustration set', 'Web page build', 'Copy editing',
  'Studio hire', 'Delivery',
];

const LINE_ITEM_ROWS: DbRow[] = rowsOf(
  'line_items',
  Array.from({ length: ROW_CAP }, (_, index) => ({
    id: index + 1,
    invoice_id: Math.floor(index / 6) + 1,
    description: LINE_ITEM_LABELS[index % LINE_ITEM_LABELS.length],
    amount: 750 + ((index * 425) % 9_250),
  })),
);

const ATTACHMENT_ROWS: DbRow[] = rowsOf(
  'attachments',
  Array.from({ length: ROW_CAP }, (_, index) => ({
    id: index + 1,
    invoice_id: Math.floor(index / 3) + 1,
    path: `attachments/inv-${String(Math.floor(index / 3) + 1).padStart(4, '0')}-${['receipt', 'timesheet', 'brief'][index % 3]}.pdf`,
  })),
);

/** Every seeded row, keyed the way the read path asks for it. */
export const SAM_ROWS: Record<string, DbRow[]> = {
  recipes: RECIPE_ROWS,
  ingredients: INGREDIENT_ROWS,
  recipe_ingredients: RECIPE_INGREDIENT_ROWS,
  saved_recipes: SAVED_RECIPE_ROWS,
  users: USER_ROWS,
  _sv_migrations: MIGRATION_ROWS,
  invoices: INVOICE_ROWS,
  line_items: LINE_ITEM_ROWS,
  clients: CLIENT_ROWS,
  attachments: ATTACHMENT_ROWS,
};

/* ------------------------------------------------------- the staged edits */

/**
 * The three pending edits S11 opens with under ?state=edits, screen spec S11.
 *
 * They are a fixture rather than something the screen makes up, for the same
 * reason every other number is: the state a reviewer opens has to be the same
 * state the next reviewer opens, and "row 3, minutes, 720 becomes 600" is a
 * demo decision, not a component decision.
 */
export interface StagedEdit {
  rowId: string;
  column: string;
  value: DbCell;
}

export const STAGED_EDITS: Record<string, StagedEdit[]> = {
  recipes: [
    { rowId: 'recipes-3', column: 'minutes', value: 600 },
    { rowId: 'recipes-8', column: 'is_public', value: true },
    { rowId: 'recipes-12', column: 'title', value: 'Smoked eggplant dip with pomegranate' },
  ],
  saved_recipes: [
    { rowId: 'saved_recipes-4', column: 'recipe_id', value: 12 },
    { rowId: 'saved_recipes-9', column: 'user_id', value: 7 },
    { rowId: 'saved_recipes-21', column: 'recipe_id', value: 3 },
  ],
  invoices: [
    { rowId: 'invoices-2', column: 'paid', value: true },
    { rowId: 'invoices-5', column: 'total', value: 18_500 },
    { rowId: 'invoices-11', column: 'client_id', value: 1 },
  ],
};

/* ------------------------------------------------------- the query runner */

/**
 * Three queries are recognized and everything else says so, screen spec S12.
 *
 * A prototype that pretended to be a database would be lying about the one
 * screen whose entire job is credibility, so the honest fallback is part of
 * the fixture rather than an afterthought. What is not faked is the answer:
 * each canned query reads the seeded rows, so the count a person sees is the
 * count the grid above it would show.
 */
export const CANNED_QUERIES = [
  'select title, servings, minutes from recipes where minutes < 30;',
  'select count(*) from saved_recipes;',
  'select * from users limit 5;',
];

/** The two queries already in the history when the panel opens, per project. */
export const QUERY_HISTORY: Record<string, SavedQuery[]> = {
  'herb-and-ash': [
    { id: 'q_saved_count', sql: 'select count(*) from saved_recipes;', at: ISO(6 * MINUTE) },
    { id: 'q_users_five', sql: 'select * from users limit 5;', at: ISO(1 * HOUR) },
  ],
};

/** Everything after the last statement separator, normalized for matching. */
function normalizeQuery(sql: string): string {
  return sql.trim().replace(/\s+/g, ' ').replace(/;+$/, '').toLowerCase();
}

/**
 * Runs one of the three canned queries against the seeded rows, or returns
 * null so the screen can say plainly that it has no answer for this one.
 */
export function cannedResult(
  sql: string,
  rowsFor: (table: string) => DbRow[],
): QueryResult | null {
  const asked = normalizeQuery(sql);

  if (asked === normalizeQuery(CANNED_QUERIES[0])) {
    const columns = ['title', 'servings', 'minutes'];
    const rows = rowsFor('recipes')
      .filter((row) => Number(row.cells.minutes) < 30)
      .map((row) => ({
        id: row.id,
        cells: { title: row.cells.title, servings: row.cells.servings, minutes: row.cells.minutes },
      }));
    return { columns, rows, ms: 12, truncated: false, sourceTable: 'recipes' };
  }

  if (asked === normalizeQuery(CANNED_QUERIES[1])) {
    return {
      columns: ['count'],
      rows: [{ id: 'count-1', cells: { count: rowsFor('saved_recipes').length } }],
      ms: 4,
      truncated: false,
      sourceTable: 'saved_recipes',
    };
  }

  if (asked === normalizeQuery(CANNED_QUERIES[2])) {
    const rows = rowsFor('users').slice(0, 5);
    return {
      columns: ['id', 'email', 'name', 'created_at'],
      rows,
      ms: 7,
      truncated: false,
      sourceTable: 'users',
    };
  }

  return null;
}

/* --------------------------------------------------- the connection details */

/**
 * What a person needs to reach their database from something that is not
 * their app. Derived from the project rather than stored beside it, so the
 * panel cannot drift from the database name the overview prints.
 */
export function connectionFor(project: Project): DbConnection {
  return {
    host: `db.singapore.${PUBLIC_DOMAIN}`,
    port: '5432',
    database: project.databaseName,
    user: `app_${project.slug.replace(/-/g, '_')}`,
    password: `svc_${project.id.replace(/^sv_prj_/, '')}_8d41a7c0b2`,
  };
}

/**
 * The database empty state's agent prompt, screen spec 5.7, verbatim.
 *
 * It is a fixture rather than a string in the screen because it is the one
 * empty state in the product whose action is a payload rather than a button,
 * and because the last two sentences are the load bearing ones: do not create
 * the database, it already exists, and do not ask for a connection string.
 * Without them an agent spends a person's afternoon provisioning a second
 * database they are already paying for.
 */
export const DATABASE_AGENT_PROMPT = `My app is hosted on SV Cloud and it already has a database connected. I need
you to create the tables it needs. Write the schema and the migration in my
code, then commit to main so it deploys. Do not create the database itself,
it already exists, and do not ask me for a connection string.`;
